import { anchorPopup_template } from "../components/templates/anchorPopup_template";
import { vnTemplate } from "../../vendor/valnet/valnet_jslib/vnKnockoutJsTemplates/vnTemplate";

declare var ko: any;
declare class ResizeObserver {
    constructor(callback: (entries: any[]) => void);
    observe(target: Element): void;
    unobserve(target: Element): void;
    disconnect(): void;
}

/**
 * Positions a floating element next to a reference element (Popper-style),
 * without adding a dependency. Uses fixed coordinates from getBoundingClientRect,
 * flips when there is not enough room, shifts to stay inside the viewport, and
 * follows scroll on the window and overflow scroll parents of the anchor.
 */

export type AnchorPopupPlacement =
    | "top" | "top-start" | "top-end"
    | "bottom" | "bottom-start" | "bottom-end"
    | "left" | "left-start" | "left-end"
    | "right" | "right-start" | "right-end";

export interface AnchorPopupPositionOptions {
    /** Default bottom-start when omitted. */
    placement?: AnchorPopupPlacement;
    /** Gap between anchor and popup (px). Default 8. */
    offset?: number;
    /** Minimum space from viewport edges (px). Default 8. */
    boundaryPadding?: number;
    /** Try the opposite side when the popup would overflow. Default true. */
    flip?: boolean;
    /** Slide so the popup stays in the viewport. Default true. */
    shift?: boolean;
}

export interface AnchorPopupPosition {
    top: number;
    left: number;
    placement: AnchorPopupPlacement;
    /**
     * Pixel distance from the popup's leading edge (left for top/bottom placements,
     * top for left/right placements) to the center of the nub arrow, always
     * computed so the nub points at the center of the anchor element.
     */
    nubOffset: number;
}

const DEFAULT_OFFSET = 8;
const DEFAULT_PADDING = 8;

function getScrollParents(node: HTMLElement | null): Array<Element | Window> {
    const roots: Array<Element | Window> = [window];
    if (!node) {
        return roots;
    }
    let el: HTMLElement | null = node.parentElement;
    while (el) {
        const style = window.getComputedStyle(el);
        const oy = style.overflowY;
        const ox = style.overflowX;
        if (/(auto|scroll|overlay)/.test(oy) || /(auto|scroll|overlay)/.test(ox)) {
            roots.push(el);
        }
        el = el.parentElement;
    }
    return roots;
}

function oppositePlacement(p: AnchorPopupPlacement): AnchorPopupPlacement {
    if (p.indexOf("top") === 0) {
        return (p.replace("top", "bottom") as AnchorPopupPlacement);
    }
    if (p.indexOf("bottom") === 0) {
        return (p.replace("bottom", "top") as AnchorPopupPlacement);
    }
    if (p.indexOf("left") === 0) {
        return (p.replace("left", "right") as AnchorPopupPlacement);
    }
    return (p.replace("right", "left") as AnchorPopupPlacement);
}

/**
 * Returns fixed viewport coordinates for the popup's top-left corner.
 */
export function computeAnchorPopupPosition(
    anchor: HTMLElement,
    popup: HTMLElement,
    opts: AnchorPopupPositionOptions
): AnchorPopupPosition {
    const offset = opts.offset != null ? opts.offset : DEFAULT_OFFSET;
    const pad = opts.boundaryPadding != null ? opts.boundaryPadding : DEFAULT_PADDING;
    const doFlip = opts.flip !== false;
    const doShift = opts.shift !== false;

    const ref = anchor.getBoundingClientRect();
    const prevPosition = popup.style.position;
    const prevLeft = popup.style.left;
    const prevTop = popup.style.top;
    const prevVisibility = popup.style.visibility;
    const prevPointer = popup.style.pointerEvents;
    popup.style.visibility = "hidden";
    popup.style.pointerEvents = "none";
    popup.style.position = "fixed";
    popup.style.left = "0px";
    popup.style.top = "0px";
    const popW = popup.offsetWidth;
    const popH = popup.offsetHeight;
    popup.style.position = prevPosition;
    popup.style.left = prevLeft;
    popup.style.top = prevTop;
    popup.style.visibility = prevVisibility;
    popup.style.pointerEvents = prevPointer;

    let placement = opts.placement != null ? opts.placement : "bottom-start";

    function computeFor(p: AnchorPopupPlacement): AnchorPopupPosition {
        let top = 0;
        let left = 0;
        if (p.indexOf("bottom") === 0) {
            top = ref.bottom + offset;
            if (p === "bottom") {
                left = ref.left + ref.width / 2 - popW / 2;
            } else if (p === "bottom-start") {
                left = ref.left;
            } else {
                left = ref.right - popW;
            }
        } else if (p.indexOf("top") === 0) {
            top = ref.top - popH - offset;
            if (p === "top") {
                left = ref.left + ref.width / 2 - popW / 2;
            } else if (p === "top-start") {
                left = ref.left;
            } else {
                left = ref.right - popW;
            }
        } else if (p.indexOf("right") === 0) {
            left = ref.right + offset;
            if (p === "right") {
                top = ref.top + ref.height / 2 - popH / 2;
            } else if (p === "right-start") {
                top = ref.top;
            } else {
                top = ref.bottom - popH;
            }
        } else {
            left = ref.left - popW - offset;
            if (p === "left") {
                top = ref.top + ref.height / 2 - popH / 2;
            } else if (p === "left-start") {
                top = ref.top;
            } else {
                top = ref.bottom - popH;
            }
        }
        return { top: top, left: left, placement: p, nubOffset: 0 };
    }

    function overflows(pos: AnchorPopupPosition): boolean {
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        return pos.left < pad
            || pos.top < pad
            || pos.left + popW > vw - pad
            || pos.top + popH > vh - pad;
    }

    function fitsOpposite(current: AnchorPopupPlacement): boolean {
        const alt = oppositePlacement(current);
        return !overflows(computeFor(alt));
    }

    let pos = computeFor(placement);
    if (doFlip && overflows(pos) && fitsOpposite(placement)) {
        placement = oppositePlacement(placement);
        pos = computeFor(placement);
    }

    const naturalLeft = pos.left;
    const naturalTop  = pos.top;

    if (doShift) {
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const minLeft = pad;
        const maxLeft = Math.max(pad, vw - popW - pad);
        const minTop = pad;
        const maxTop = Math.max(pad, vh - popH - pad);
        if (pos.left < minLeft) {
            pos.left = minLeft;
        }
        if (pos.left > maxLeft) {
            pos.left = maxLeft;
        }
        if (pos.top < minTop) {
            pos.top = minTop;
        }
        if (pos.top > maxTop) {
            pos.top = maxTop;
        }
    }

    // Always compute the nub offset so the arrow consistently points at the
    // center of the anchor element, whether or not a viewport shift occurred.
    const isVerticalPlacement = placement.startsWith("bottom") || placement.startsWith("top");
    let nubOffset: number;
    if (isVerticalPlacement) {
        const cx = ref.left + ref.width / 2;
        nubOffset = Math.max(14, Math.min(popW - 14, cx - pos.left));
    } else {
        const cy = ref.top + ref.height / 2;
        nubOffset = Math.max(14, Math.min(popH - 14, cy - pos.top));
    }
    pos.nubOffset = nubOffset;

    return pos;
}

export interface AnchorPopupOptions extends AnchorPopupPositionOptions {
    /** Where the popup node is attached. Default document.body. */
    container?: HTMLElement;
    /** Close when clicking outside the popup layer. Default true. */
    closeOnOutsideClick?: boolean;
    /** Close on clicking the anchor. Default false. */
    closeOnAnchorClick?: boolean;
    /** Close on Escape. Default true. */
    closeOnEscape?: boolean;
    /** z-index for the floating layer. Default 9999997. */
    zIndex?: number;
    /** Called when the Cancel button is clicked. */
    onCancel?: () => void;
    /** Called when the Submit button is clicked. */
    onSubmit?: () => void;
    /** Close the popup via the close button or clicking outside the popup */
    onClose?: () => void;
    /**
     * CSS selector for elements whose primary mousedown should not dismiss the popup (e.g. Insert buttons
     * that use `click` — document capture `mousedown` would otherwise run first and close before `click` fires).
     */
    outsidePointerIgnoreSelector?: string;
    /**
     * HTML for the form body when using the options-only constructor.
     * Same as assigning `instance.content` after construction.
     * Ignored when contentTemplate is provided.
     */
    content?: string;
    /**
     * A vnTemplate instance whose element will be built, KO-bound, and injected
     * into the popup body. Takes precedence over `content`.
     */
    contentTemplate?: vnTemplate;
    /** className on the auto-created wrapper. Default "anchor-popup". */
    wrapperClassName?: string;
    /** Show the × close button in the shell. Default true. */
    showClose?: boolean;
    /** Show the Submit button. Default true. */
    showSubmit?: boolean;
    /** Show the Cancel button. Default true. */
    showCancel?: boolean;
    /** Label for the Cancel button. Default "Cancel". */
    cancelButtonText?: string;
    /** Label for the Submit button. Default "Submit". */
    submitButtonText?: string;
    /**
     * When true, a full-viewport semi-transparent overlay is shown behind the popup
     * (and removed when the popup hides). Useful for drawing focus to the popup.
     */
    overlay?: boolean;
}

/**
 * Manages one floating layer next to an anchor.
 *
 * `new AnchorPopup(anchor, element, options?)` — you supply the root element.
 * `new AnchorPopup(anchor, options?)` — Knockout chrome via anchorPopup_template (close, body, Cancel / OK).
 *
 * Outside-click close: any mousedown not contained in the popup layer closes it (including clicks on the anchor).
 */
export class AnchorPopup {
    private static nextInstanceId = 0;

    private anchor: HTMLElement;
    private popup: HTMLElement;
    private container: HTMLElement;
    private options: AnchorPopupOptions;
    private boundUpdate: () => void;
    private boundOutside: (e: MouseEvent) => void;
    private boundKey: (e: KeyboardEvent) => void;
    private boundAnchorClick: (e: MouseEvent) => void;
    /** pagehide (unload) + popstate (browser back/forward without full navigation). */
    private readonly boundDismissOnBrowseAway: () => void;
    private anchorPopupKoTemplate: anchorPopup_template | null = null;
    private koViewModelHolder: any = null;
    private scrollParents: Array<Element | Window> = [];
    private visible = false;
    /** Breakpoint below which the popup renders as a fixed bottom sheet instead of an anchor-relative tooltip. */
    private readonly bottomSheetMq: MediaQueryList = window.matchMedia('(max-width: 640px)');
    private overlayEl: HTMLElement | null = null;
    private popupResizeObserver: ResizeObserver | null = null;

    constructor(anchor: HTMLElement, options?: AnchorPopupOptions);
    constructor(anchor: HTMLElement, popup: HTMLElement, options?: AnchorPopupOptions);
    constructor(anchor: HTMLElement, popupOrOptions?: HTMLElement | AnchorPopupOptions, options?: AnchorPopupOptions) {
        this.anchor = anchor;
        let opts: AnchorPopupOptions;
        if (popupOrOptions instanceof HTMLElement) {
            this.popup = popupOrOptions;
            opts = options ? options : {};
        } else {
            opts = popupOrOptions ? popupOrOptions : {};
            const cls = opts.wrapperClassName != null ? opts.wrapperClassName : "anchor-popup";
            const uniqueId = "anchorPopup_" + String(AnchorPopup.nextInstanceId++);
            this.koViewModelHolder = {};
            this.anchorPopupKoTemplate = new anchorPopup_template(uniqueId, this.koViewModelHolder, this);
            this.popup = this.anchorPopupKoTemplate.build([cls]);
            ko.applyBindings(this.koViewModelHolder, this.popup);
            const vm = this.anchorPopupKoTemplate.getTemplateViewModel();
            vm.bodyHtml(opts.content != null ? opts.content : "");
            vm.showClose(opts.showClose !== false);
            vm.submitButtonVisible(opts.showSubmit !== false);
            vm.cancelButtonVisible(opts.showCancel !== false);
            if (opts.cancelButtonText != null) {
                vm.cancelButtonText(opts.cancelButtonText);
            }
            if (opts.submitButtonText != null) {
                vm.submitButtonText(opts.submitButtonText);
            }
            if (opts.contentTemplate != null) {
                const bodyEl = this.popup.querySelector(".anchor-popup__body") as HTMLElement | null;
                if (bodyEl) {
                    const tplEl = opts.contentTemplate.build();
                    ko.applyBindings(opts.contentTemplate.viewModel, tplEl);
                    bodyEl.innerHTML = "";
                    bodyEl.appendChild(tplEl);
                }
            }
        }
        if (!opts.placement) {
            opts.placement = "bottom-start";
        }
        this.options = opts;
        this.container = this.options.container || document.body;
        this.boundUpdate = this.update.bind(this);
        this.boundOutside = this.onOutsidePointer.bind(this);
        this.boundKey = this.onKeyDown.bind(this);
        this.boundDismissOnBrowseAway = this.onBrowseAway.bind(this);
        this.boundAnchorClick = this.onAnchorClick.bind(this);
    }

    /**
     * HTML in the form body (managed mode) or the full root (external element).
     * innerHTML only — use trusted strings.
     */
    public get content(): string {
        if (this.anchorPopupKoTemplate) {
            return this.anchorPopupKoTemplate.getTemplateViewModel().bodyHtml();
        }
        return this.popup.innerHTML;
    }

    public set content(html: string) {
        if (this.anchorPopupKoTemplate) {
            this.anchorPopupKoTemplate.getTemplateViewModel().bodyHtml(html);
        } else {
            this.popup.innerHTML = html;
        }
        if (this.visible) {
            this.update();
        }
    }

    public getLayerElement(): HTMLElement {
        return this.popup;
    }

    public open(): void {
        if (this.visible) {
            this.update();
            return;
        }
        this.visible = true;
        this.popup.style.display = "";
        const z = this.options.zIndex != null ? this.options.zIndex : 9999997;
        this.popup.style.position = "fixed";
        this.popup.style.zIndex = String(z);
        this.showOverlay(z);
        if (this.popup.parentNode !== this.container) {
            this.container.appendChild(this.popup);
        }
        this.update();
        this.setupScrollListeners();
        this.popupResizeObserver = new ResizeObserver(() => this.update());
        this.popupResizeObserver.observe(this.popup);
        window.addEventListener("resize", this.boundUpdate);
        if (this.options.closeOnOutsideClick !== false) {
            document.addEventListener("mousedown", this.boundOutside, true);
        }
        if (this.options.closeOnEscape !== false) {
            document.addEventListener("keydown", this.boundKey, true);
        }
        window.addEventListener("pagehide", this.boundDismissOnBrowseAway);
        window.addEventListener("popstate", this.boundDismissOnBrowseAway);
        if (this.options.closeOnAnchorClick !== false) {
            this.anchor.addEventListener("click", this.boundAnchorClick, true);
        }
    }

    public update(): void {
        if (!this.visible) {
            return;
        }
        if (this.bottomSheetMq.matches) {
            this.popup.classList.add('anchor-popup--bottom-sheet');
            // Clear any inline coordinates set during a previous desktop layout
            this.popup.style.left = "";
            this.popup.style.top = "";
            return;
        }
        this.popup.classList.remove('anchor-popup--bottom-sheet');
        const pos = computeAnchorPopupPosition(this.anchor, this.popup, {
            placement: this.options.placement,
            offset: this.options.offset,
            boundaryPadding: this.options.boundaryPadding,
            flip: this.options.flip,
            shift: this.options.shift
        });
        this.popup.style.left = pos.left + "px";
        this.popup.style.top = pos.top + "px";
        this.popup.setAttribute("data-placement", pos.placement);
        this.applyNubOffset(pos);
    }

    /**
     * Overrides the CSS nub position when the popup was shifted to stay inside the
     * viewport, so the nub still points at the anchor center. When no shift occurred,
     * all inline overrides are cleared and CSS handles the default nub position.
     */
    private applyNubOffset(pos: AnchorPopupPosition): void {
        const nub = this.popup.querySelector(".anchor-popup__nub") as HTMLElement | null;
        if (!nub) { return; }

        const placement = pos.placement;
        if (placement.startsWith("bottom") || placement.startsWith("top")) {
            // Clear any stale vertical overrides from a prior left/right placement
            nub.style.top = nub.style.bottom = "";
            nub.style.marginTop = nub.style.marginBottom = "";
            // Position nub horizontally to point at the anchor center
            nub.style.left = pos.nubOffset + "px";
            nub.style.right = "";
            nub.style.marginLeft = "-7px";
            nub.style.marginRight = "";
        } else {
            // Clear any stale horizontal overrides from a prior bottom/top placement
            nub.style.left = nub.style.right = "";
            nub.style.marginLeft = nub.style.marginRight = "";
            // Position nub vertically to point at the anchor center
            nub.style.top = pos.nubOffset + "px";
            nub.style.bottom = "";
            nub.style.marginTop = "-7px";
            nub.style.marginBottom = "";
        }
    }

    /**
     * Transitions the popup to a new anchor and config without tearing it down.
     * Only the properties included in `options` are changed; everything else is inherited.
     * Useful for multi-step flows where the popup moves from one element to another.
     */
    public next(anchor: HTMLElement, options: AnchorPopupOptions): void {
        if (this.visible) {
            // Tear down all listeners tied to the current anchor / options
            this.hideOverlay();
            if (this.options.closeOnAnchorClick !== false) {
                this.anchor.removeEventListener("click", this.boundAnchorClick, true);
            }
            document.removeEventListener("mousedown", this.boundOutside, true);
            document.removeEventListener("keydown", this.boundKey, true);
            this.teardownScrollListeners();
        }

        // Replace options and apply viewmodel changes
        this.options = options;
        if (this.anchorPopupKoTemplate) {
            const vm = this.anchorPopupKoTemplate.getTemplateViewModel();
            if (options.content != null)           { vm.bodyHtml(options.content); }
            if (options.cancelButtonText != null)  { vm.cancelButtonText(options.cancelButtonText); }
            if (options.submitButtonText != null)  { vm.submitButtonText(options.submitButtonText); }
            if (options.showClose != null)         { vm.showClose(options.showClose); }
            if (options.showSubmit != null)        { vm.submitButtonVisible(options.showSubmit); }
            if (options.showCancel != null)        { vm.cancelButtonVisible(options.showCancel); }
        }

        this.anchor = anchor;

        if (!this.visible) {
            // Not yet open — open() handles all setup from scratch
            this.open();
            return;
        }

        // Re-establish overlay and listeners per new options, then reposition
        if (this.options.overlay) {
            const z = this.options.zIndex != null ? this.options.zIndex : 9999997;
            this.showOverlay(z);
        }
        if (this.options.closeOnOutsideClick !== false) {
            document.addEventListener("mousedown", this.boundOutside, true);
        }
        if (this.options.closeOnEscape !== false) {
            document.addEventListener("keydown", this.boundKey, true);
        }
        this.setupScrollListeners();
        if (this.options.closeOnAnchorClick !== false) {
            this.anchor.addEventListener("click", this.boundAnchorClick, true);
        }
        this.update();
    }

    public close(): void {
        this.destroy();
    }

    private destroy(): void {
        if (!this.visible) {
            return;
        }
        this.visible = false;
        this.teardownScrollListeners();
        if (this.popupResizeObserver) {
            this.popupResizeObserver.disconnect();
            this.popupResizeObserver = null;
        }
        window.removeEventListener("resize", this.boundUpdate);
        if (this.options.closeOnAnchorClick !== false) {
            this.anchor.removeEventListener("click", this.boundAnchorClick, true);
        }
        window.removeEventListener("pagehide", this.boundDismissOnBrowseAway);
        window.removeEventListener("popstate", this.boundDismissOnBrowseAway);
        document.removeEventListener("mousedown", this.boundOutside, true);
        document.removeEventListener("keydown", this.boundKey, true);
        if (this.anchorPopupKoTemplate && typeof ko !== "undefined" && ko.cleanNode) {
            ko.cleanNode(this.popup);
        }
        if (this.popup.parentNode) {
            this.popup.parentNode.removeChild(this.popup);
        }
        this.hideOverlay();
        this.anchorPopupKoTemplate = null;
        this.koViewModelHolder = null;
    }

    public isVisible(): boolean {
        return this.visible;
    }

    private showOverlay(popupZ: number): void {
        if (!this.options.overlay) { return; }
        if (this.overlayEl) { return; }
        const el = document.createElement('div');
        el.style.cssText = `position:fixed;inset:0;z-index:${popupZ - 1};background:rgba(0,0,0,0.55);pointer-events:auto;`;
        this.container.appendChild(el);
        this.overlayEl = el;
    }

    private hideOverlay(): void {
        if (this.overlayEl && this.overlayEl.parentNode) {
            this.overlayEl.parentNode.removeChild(this.overlayEl);
        }
        this.overlayEl = null;
    }

    private setupScrollListeners(): void {
        this.scrollParents = getScrollParents(this.anchor);
        for (let i = 0; i < this.scrollParents.length; i++) {
            const r = this.scrollParents[i];
            if (r === window) {
                window.addEventListener("scroll", this.boundUpdate, true);
            } else {
                (r as Element).addEventListener("scroll", this.boundUpdate, true);
            }
        }
    }

    private teardownScrollListeners(): void {
        for (let i = 0; i < this.scrollParents.length; i++) {
            const r = this.scrollParents[i];
            if (r === window) {
                window.removeEventListener("scroll", this.boundUpdate, true);
            } else {
                (r as Element).removeEventListener("scroll", this.boundUpdate, true);
            }
        }
        this.scrollParents = [];
    }

    private onAnchorClick(e: MouseEvent): void {
        this.destroy();
    }

    private onOutsidePointer(e: MouseEvent): void {
        // Only primary-button down should dismiss; right/middle click opens native menus without closing.
        if (e.button !== 0) {
            return;
        }
        const ignoreSel = this.options.outsidePointerIgnoreSelector;
        if (ignoreSel && e.target instanceof Element && e.target.closest(ignoreSel)) {
            return;
        }
        const t = e.target as Node;
        if (this.popup.contains(t)) {
            return;
        }
        if(this.options.onClose) {
            this.options.onClose();
        }
        this.destroy();
    }

    private onKeyDown(e: KeyboardEvent): void {
        if (e.keyCode === 27) {
            this.runCloseAction();
        }
    }

    /** Full unload or history navigation — full teardown without lifecycle callbacks. */
    private onBrowseAway(): void {
        this.destroy();
    }

    public anchorPopupSubmitClose(): void {
        if (this.options.onClose) {
            this.options.onClose();
        }
        this.destroy();
    }

    /** Called from anchorPopup_template (footer Cancel button). */
    public anchorPopupSubmitCancel(): void {
        if (this.options.onCancel) {
            this.options.onCancel();
        }
        this.destroy();
    }

    /** Called from anchorPopup_template (footer Submit button). */
    public anchorPopupSubmitSubmit(): void {
        if (this.options.onSubmit) {
            this.options.onSubmit();
        }
        this.destroy();
    }

    /**
     * Show or hide the loading spinner inside the managed-chrome popup.
     * No-op when using the external-element constructor.
     */
    public setLoading(loading: boolean): void {
        if (this.anchorPopupKoTemplate) {
            this.anchorPopupKoTemplate.getTemplateViewModel().loading(loading);
        }
    }

    /**
     * Show or hide the Submit button.
     * No-op when using the external-element constructor.
     */
    public setSubmitVisible(visible: boolean): void {
        if (this.anchorPopupKoTemplate) {
            this.anchorPopupKoTemplate.getTemplateViewModel().submitButtonVisible(visible);
        }
    }

    private runCloseAction(): void {
        if (this.options.onCancel) {
            this.options.onCancel();
        }
        this.destroy();
    }
}



// WEBPACK FOOTER //
// ./js/utils/AnchorPopup.ts